import React, { useEffect, createContext, useState, useMemo, useRef, useContext } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { QUERY_BY_ID } from '@/graphql/queries/orders';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useClientUser } from '@/hooks';
import { UserContext } from '@/components/App';
import { DUPLICATE_PRICING_TARIFF, CREATE_TARIFF, DELETE_TARIFF } from '@/components/Account/Tariffs/graphql';

import { LIST_GLOBAL_TARIFFS } from './graphql';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { admin_clients } = useContext(UserContext);
    const { isOnwardAdmin, isCorporateAdmin, organization_id } = useClientUser();

    // The org admin's child accounts (shippers + carriers). Used to widen the tariff list
    // beyond org-level (client_id null) tariffs to include each child account's own tariffs.
    const orgClientIds = useMemo(() => (admin_clients || []).map((c) => c.client_id), [admin_clients]);

    const query = useMemo(() => {
        if (isOnwardAdmin) {
            return { _and: [{ client_id: { _is_null: true } }, { organization_id: { _is_null: true } }] };
        }
        if (isCorporateAdmin) {
            // Org admins manage every tariff in the org
            return {
                _or: [{ client_id: { _in: orgClientIds } }, { organization_id: { _eq: organization_id } }],
            };
        }
        return null;
    }, [isOnwardAdmin, isCorporateAdmin, organization_id, orgClientIds]);

    const { data, loading: listInflight } = useQuery(LIST_GLOBAL_TARIFFS, {
        skip: !query,
        variables: {
            where: query,
        },
        onError: (e) => {
            console.log(e);
            Sentry.captureException(e);
        },
    });

    const [createTariff] = useMutation(CREATE_TARIFF, {
        onCompleted: (resp) => {
            if (resp?.tariffs?.returning.length > 0) {
                navigate(`/account/tariff/${resp.tariffs.returning[0].tariff_id}`);
            }
        },
        onError: (e) => {
            console.log(e);
            Sentry.captureException(e);
        },
    });

    const [duplicateTariff] = useMutation(DUPLICATE_PRICING_TARIFF, {
        onCompleted: (resp) => {
            if (resp?.duplicated.length > 0) {
                navigate(`/account/tariff/${resp.duplicated[0].tariff_id}`);
            }
        },
        onError: (e) => {
            console.log(e);
            Sentry.captureException(e);
        },
    });

    const [deleteTariff, { loading: deleteLoading }] = useMutation(DELETE_TARIFF, {
        update: (cache, { data: { deleted } }) => {
            cache.updateQuery(
                {
                    query: LIST_GLOBAL_TARIFFS,
                    variables: {
                        where: query,
                    },
                },
                (data) => {
                    const { tariffs } = data;
                    let clone = [...tariffs];
                    clone = clone.filter((tariff) => tariff.tariff_id !== deleted.tariff_id);

                    return {
                        tariffs: clone,
                    };
                }
            );
        },
        onError: (e) => {
            console.log(e);
            Sentry.captureException(e);
        },
    });

    const tariffs = useMemo(() => {
        return data?.tariffs ? data.tariffs : [];
    }, [data]);

    return (
        <Context.Provider
            value={{
                state: {
                    tariffs,
                    admin_clients: admin_clients || [],
                },
                loading: {
                    init: listInflight,
                    delete: deleteLoading,
                },
                callbacks: {
                    deleteTariff: (tariff_id) => {
                        deleteTariff({
                            variables: {
                                tariff_id,
                            },
                        });
                    },
                    duplicateTariff: (tariff_id) => {
                        duplicateTariff({
                            variables: {
                                tariff_id,
                            },
                        });
                    },
                    // `fields` comes from the tariff library: algo_type, category, commodity_tags.
                    createTariff: (fields) => {
                        createTariff({
                            variables: {
                                tariffs: [
                                    {
                                        client_id: null,
                                        ...(isCorporateAdmin ? { organization_id } : {}),
                                        tariff_type: 'ORDER',
                                        ...fields,
                                    },
                                ],
                            },
                        });
                    },
                },
                errors: {},
            }}
        >
            {children}
        </Context.Provider>
    );
};
